/**
 * Video caching service using IndexedDB
 * Downloads and caches proxy videos locally for offline access and performance
 *
 * Architecture:
 * - Memory Cache (LRU): Hot blobs for instant access
 * - IndexedDB: Persistent storage across sessions
 * - Background caching: Avoids double downloads
 * - Blob URL lifecycle: Automatic cleanup to prevent memory leaks
 */

import { getStorageUrl } from './api'
import { recordTimelinePlaybackPerf } from './perfHooks'
import { classifyPlaybackMediaPath } from './playback/playbackDiagnostics'
import { isTimelinePlaybackActive, schedulePlaybackAwareBackgroundWork } from './playbackActivity'
import type { VideoPlaybackSource } from './videoPlaybackSource'

/** Strip query string and fragment from a URL for safe logging (avoids leaking signed tokens). */
function safeUrlForLog(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return url.split('?')[0]
  }
}

// =============================================================================
// Offline Mode (Play from device)
// =============================================================================

const OFFLINE_MODE_KEY = 'martini-offline-mode'
export const OFFLINE_MODE_CHANGED_EVENT = 'offline-mode-changed'

let cachedOfflineMode: boolean | null = null

/**
 * Get the current offline mode setting.
 * When enabled, getSinkUrl() will always check IndexedDB before falling back to network.
 */
export function getOfflineMode(): boolean {
  if (typeof window === 'undefined') return false

  if (cachedOfflineMode !== null) return cachedOfflineMode

  cachedOfflineMode = localStorage.getItem(OFFLINE_MODE_KEY) === 'true'
  return cachedOfflineMode
}

/**
 * Set the offline mode.
 */
export function setOfflineMode(enabled: boolean): void {
  if (typeof window === 'undefined') return

  cachedOfflineMode = enabled
  localStorage.setItem(OFFLINE_MODE_KEY, String(enabled))
  window.dispatchEvent(new CustomEvent(OFFLINE_MODE_CHANGED_EVENT, { detail: enabled }))
}

/**
 * Guard for using IndexedDB exact hits on the active playback path while
 * "Play from device" is off. This is intentionally opt-in until Sharkey
 * baselines prove that local Blob readiness beats direct byte-range playback.
 */
export function isActiveIndexedDBExactHitEnabled(): boolean {
  if (process.env.NEXT_PUBLIC_PLAYBACK_IDB_EXACT_HIT === '1') return true
  if (typeof window === 'undefined') return false
  return localStorage.getItem(ACTIVE_EXACT_HIT_POLICY_KEY) === 'true'
}

function trimQueryAndHash(value: string): string {
  const queryIndex = value.search(/[?#]/)
  return queryIndex >= 0 ? value.slice(0, queryIndex) : value
}

function normalizeStoragePath(pathname: string): string | null {
  let path = trimQueryAndHash(pathname).replace(/\\/g, '/').replace(/^\/+/, '')
  if (!path) return null

  if (path.startsWith('_storage/')) path = path.slice('_storage/'.length)
  if (path.startsWith('storage/')) path = path.slice('storage/'.length)

  try {
    path = decodeURI(path)
  } catch {
    // Keep the original path if it contains malformed escapes.
  }

  path = path.replace(/\/+/g, '/')
  if (!path) return null

  if (/^v\d+\.\d+\/(?:assets|tmp|sets|exports|projects)\//i.test(path)) {
    return path
  }
  if (/^(?:assets|tmp|sets|exports|projects)\//i.test(path)) {
    return path
  }
  return null
}

/**
 * Canonical storage identity for cache validation. It strips volatile signed
 * query strings and ignores storage/CDN host aliases while keeping the stable
 * object path that distinguishes active versions and regenerated proxies.
 */
export function getVideoCacheStorageIdentity(uriOrUrl?: string): string | null {
  if (!uriOrUrl || uriOrUrl.startsWith('blob:') || uriOrUrl.startsWith('data:')) return null

  try {
    const parsed = new URL(uriOrUrl, typeof window !== 'undefined' ? window.location.href : 'http://localhost')
    if (parsed.pathname.includes('/api/proxy-media')) {
      const proxiedUrl = parsed.searchParams.get('url')
      return proxiedUrl ? getVideoCacheStorageIdentity(proxiedUrl) : null
    }
    const path = normalizeStoragePath(parsed.pathname)
    return path ? `storage:${path}` : null
  } catch {
    const path = normalizeStoragePath(uriOrUrl)
    return path ? `storage:${path}` : null
  }
}

function makeCacheMetadata(id: string, blob: Blob, contentType: string, sourceUrl?: string): CachedVideoMetadata {
  const storageIdentity = getVideoCacheStorageIdentity(sourceUrl) ?? undefined
  return {
    cacheKey: getVideoCacheKey(id, sourceUrl),
    id,
    contentType,
    cachedAt: Date.now(),
    sourceUrl,
    storageIdentity,
    size: blob.size,
    schemaVersion: 2,
  }
}

function hashCacheKeyPart(value: string): string {
  let hash = 2166136261
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

/**
 * Stable local cache key. Storage-backed media uses the canonical object path
 * so signed URLs and host aliases share one entry; non-storage URLs fall back
 * to a hash of the exact URL so correctness is preserved without exposing
 * credentials in diagnostics.
 */
export function getVideoCacheKey(videoId: string, sourceUrl?: string): string {
  const storageIdentity = getVideoCacheStorageIdentity(sourceUrl)
  if (storageIdentity) return `${videoId}::${storageIdentity}`
  if (sourceUrl) return `${videoId}::url:${hashCacheKeyPart(sourceUrl)}`
  return `${videoId}::legacy`
}

export type VideoCachePlaybackSource = Pick<VideoPlaybackSource, 'videoId' | 'url' | 'key'>

export function getVideoCacheKeyForSource(source: VideoCachePlaybackSource): string {
  return getVideoCacheKey(source.videoId, source.url)
}

export function isVideoCacheMetadataExactHit(
  metadata: Pick<CachedVideoMetadata, 'id' | 'sourceUrl' | 'storageIdentity'> | null,
  videoId: string,
  sourceUrl: string,
): boolean {
  if (!metadata || metadata.id !== videoId) return false
  const currentIdentity = getVideoCacheStorageIdentity(sourceUrl)
  if (metadata.storageIdentity && currentIdentity) return metadata.storageIdentity === currentIdentity
  return !!metadata.sourceUrl && metadata.sourceUrl === sourceUrl
}

function stripProxyResolution(identity: string): string {
  return identity.replace(/-\d+p-proxy\.mp4$/i, '-proxy.mp4')
}

export function getVideoCacheProxyResolution(uriOrIdentity?: string): string | null {
  if (!uriOrIdentity) return null
  const match = trimQueryAndHash(uriOrIdentity).match(/-(\d+p)-proxy\.mp4$/i)
  return match?.[1] ?? null
}

export function getVideoCacheMetadataMismatchReason(
  metadata: Pick<CachedVideoMetadata, 'id' | 'sourceUrl' | 'storageIdentity'> | null,
  videoId: string,
  sourceUrl: string,
): string {
  if (!metadata) return 'metadata-miss'
  if (metadata.id !== videoId) return 'asset-id-mismatch'

  const currentIdentity = getVideoCacheStorageIdentity(sourceUrl)
  if (metadata.storageIdentity && currentIdentity) {
    if (metadata.storageIdentity === currentIdentity) return 'exact-hit'
    if (stripProxyResolution(metadata.storageIdentity) === stripProxyResolution(currentIdentity)) {
      return 'storage-resolution-mismatch'
    }
    return 'storage-identity-mismatch'
  }

  if (metadata.sourceUrl && metadata.sourceUrl !== sourceUrl) return 'source-url-mismatch'
  return 'metadata-stale'
}

function isCachedVideoExactHit(cached: CachedVideo, videoId: string, sourceUrl: string): boolean {
  return isVideoCacheMetadataExactHit(
    {
      id: cached.id,
      sourceUrl: cached.sourceUrl,
      storageIdentity: cached.storageIdentity ?? getVideoCacheStorageIdentity(cached.sourceUrl) ?? undefined,
    },
    videoId,
    sourceUrl,
  )
}

const DB_NAME = 'martini-video-cache'
const DB_VERSION = 3
const STORE_NAME = 'videos'
const METADATA_STORE_NAME = 'videoMetadata'
const CACHE_KEY_STORE_NAME = 'videosByCacheKey'
const CACHE_KEY_METADATA_STORE_NAME = 'videoMetadataByCacheKey'
const CACHE_KEY_VIDEO_ID_INDEX = 'by-video-id'
const CACHE_KEY_STORAGE_IDENTITY_INDEX = 'by-storage-identity'
const ACTIVE_EXACT_HIT_POLICY_KEY = 'martini-playback-idb-exact-hit'

// Memory cache configuration
const MEMORY_CACHE_MAX_BLOBS = 10 // Keep last 10 blobs in memory for instant access
const MEMORY_CACHE_MAX_SIZE_MB = 1500 // Max 1.5GB in memory cache (supports multiple large videos)

interface CachedVideo {
  cacheKey?: string
  id: string
  blob: Blob
  contentType: string
  cachedAt: number
  sourceUrl?: string
  storageIdentity?: string
}

export interface CachedVideoMetadata {
  cacheKey?: string
  id: string
  contentType: string
  cachedAt: number
  sourceUrl?: string
  storageIdentity?: string
  size: number
  schemaVersion: 1 | 2
}

interface MemoryCacheEntry {
  blob: Blob
  size: number
  lastUsed: number
  sourceUrl?: string
}

type VideoCacheSource = 'memory-blob' | 'indexeddb-exact-hit' | 'indexeddb-stale-offline-hit' | 'direct-remote-url'
type BackgroundCacheStatus = 'already-in-progress' | 'started' | 'skipped'
type CacheMetadataStatus = 'disabled' | 'hit' | 'miss' | 'stale' | 'unknown' | 'error'
type CachePolicy = 'default' | 'active-exact-hit'

type CacheResolutionDiagnostics = {
  backgroundCacheStatus?: BackgroundCacheStatus
  blobUrlReplaced?: boolean
  cachePolicy?: CachePolicy
  sourceKey?: string
  cacheKey?: string
  storageIdentity?: string | null
  indexedDBMetadataStatus?: CacheMetadataStatus
  indexedDBMetadataMs?: number
  indexedDBBlobReadMs?: number
  indexedDBSkippedReason?: string
  requestedProxyResolution?: string | null
  cachedProxyResolution?: string | null
  cacheIdentityMismatchReason?: string
}

export type ExactVideoCacheDiagnostic = {
  videoId: string
  sourceKey?: string
  cacheKey: string
  storageIdentity: string | null
  status: 'exact' | 'stale' | 'miss' | 'error'
  requestedProxyResolution: string | null
  cachedProxyResolution: string | null
  mismatchReason?: string
}

export type ExactVideoCacheFillResult = {
  videoId: string
  sourceKey?: string
  cacheKey: string
  status: 'already-cached' | 'cached' | 'deduped-in-progress' | 'failed' | 'quota-exceeded'
  elapsedMs: number
  fetchMs?: number
  blobMs?: number
  writeMs?: number
  bytes?: number
  requestedProxyResolution: string | null
  pathKind: string
  errorName?: string
}

let dbPromise: Promise<IDBDatabase> | null = null
let backgroundCacheCounter = 0

// =============================================================================
// Memory Cache (LRU)
// =============================================================================

const memoryCache = new Map<string, MemoryCacheEntry>()
let memoryCacheSize = 0 // Total size in bytes

function isMemoryEntryExactHit(entry: MemoryCacheEntry, videoId: string, sourceUrl?: string): boolean {
  if (!sourceUrl) return true
  if (!entry.sourceUrl) return false
  return isVideoCacheMetadataExactHit(
    {
      id: videoId,
      sourceUrl: entry.sourceUrl,
      storageIdentity: getVideoCacheStorageIdentity(entry.sourceUrl) ?? undefined,
    },
    videoId,
    sourceUrl,
  )
}

/**
 * Add a blob to the memory cache with LRU eviction
 */
export function addToMemoryCache(videoId: string, blob: Blob, sourceUrl?: string): void {
  const size = blob.size
  const cacheKey = getVideoCacheKey(videoId, sourceUrl)

  // Replace existing entry first so memory accounting stays accurate.
  const existing = memoryCache.get(cacheKey)
  if (existing) {
    memoryCacheSize -= existing.size
    memoryCache.delete(cacheKey)
  }

  // If blob is larger than max cache size, skip caching to avoid infinite eviction loop
  if (size > MEMORY_CACHE_MAX_SIZE_MB * 1024 * 1024) {
    console.warn(
      `[VideoCache] Blob too large for memory cache: ${videoId.slice(0, 8)}... ` +
        `(${(size / 1024 / 1024).toFixed(1)}MB > ${MEMORY_CACHE_MAX_SIZE_MB}MB limit)`,
    )
    return
  }

  // Evict LRU entries if needed (with safety limit to prevent infinite loops)
  let evictionAttempts = 0
  while (
    (memoryCache.size >= MEMORY_CACHE_MAX_BLOBS || memoryCacheSize + size > MEMORY_CACHE_MAX_SIZE_MB * 1024 * 1024) &&
    memoryCache.size > 0 &&
    evictionAttempts < MEMORY_CACHE_MAX_BLOBS
  ) {
    evictLRU()
    evictionAttempts++
  }

  // Add to cache
  memoryCache.set(cacheKey, {
    blob,
    size,
    lastUsed: performance.now(),
    sourceUrl,
  })
  memoryCacheSize += size
}

/**
 * Get a blob from memory cache and update LRU timestamp
 */
function getFromMemoryCache(videoId: string, sourceUrl?: string): Blob | null {
  const cacheKeys = [getVideoCacheKey(videoId, sourceUrl), videoId]
  for (const cacheKey of cacheKeys) {
    const entry = memoryCache.get(cacheKey)
    if (!entry) continue
    if (!isMemoryEntryExactHit(entry, videoId, sourceUrl)) {
      // Stale cache entry for this exact media object.
      memoryCacheSize -= entry.size
      memoryCache.delete(cacheKey)
      continue
    }
    entry.lastUsed = performance.now()
    return entry.blob
  }
  return null
}

/**
 * Evict the least recently used entry from memory cache
 */
function evictLRU(): void {
  let oldestId = ''
  let oldestTime = Infinity

  memoryCache.forEach((entry, id) => {
    if (entry.lastUsed < oldestTime) {
      oldestTime = entry.lastUsed
      oldestId = id
    }
  })

  if (oldestId) {
    const entry = memoryCache.get(oldestId)!
    memoryCache.delete(oldestId)
    memoryCacheSize -= entry.size
  }
}

/**
 * Clear all entries from memory cache
 */
export function clearMemoryCache(): void {
  cancelAllDeferredBackgroundCaches()
  memoryCache.clear()
  memoryCacheSize = 0
}

// =============================================================================
// Background Caching
// =============================================================================

const cachingInProgress = new Set<string>()
const deferredBackgroundCacheCancels = new Map<string, () => void>()

function cancelDeferredBackgroundCache(cacheKey: string): void {
  const cancel = deferredBackgroundCacheCancels.get(cacheKey)
  if (!cancel) return
  cancel()
  deferredBackgroundCacheCancels.delete(cacheKey)
}

function cancelDeferredBackgroundCachesForVideo(videoId: string, sourceUrl?: string): void {
  if (sourceUrl) {
    cancelDeferredBackgroundCache(getVideoCacheKey(videoId, sourceUrl))
    return
  }

  const keyPrefix = `${videoId}::`
  for (const cacheKey of [...deferredBackgroundCacheCancels.keys()]) {
    if (cacheKey.startsWith(keyPrefix)) cancelDeferredBackgroundCache(cacheKey)
  }
}

function cancelAllDeferredBackgroundCaches(): void {
  for (const cacheKey of [...deferredBackgroundCacheCancels.keys()]) cancelDeferredBackgroundCache(cacheKey)
}

/**
 * Mark a video as "caching in progress" so backgroundCacheFromUrl skips it.
 * Used by the bulk downloader to prevent double-downloads.
 */
export function markCachingInProgress(videoId: string, sourceUrl?: string): void {
  cachingInProgress.add(sourceUrl ? getVideoCacheKey(videoId, sourceUrl) : videoId)
}

/**
 * Unmark a video as "caching in progress".
 */
export function unmarkCachingInProgress(videoId: string, sourceUrl?: string): void {
  cachingInProgress.delete(sourceUrl ? getVideoCacheKey(videoId, sourceUrl) : videoId)
}

/**
 * Background cache a video from URL to IndexedDB
 * Deduplicates concurrent cache requests for the same video
 */
function recordBackgroundCacheEvent(
  videoId: string,
  cacheId: string,
  action: string,
  details: {
    elapsedMs?: number
    stage?: string
    reason?: string
    status?: number
    bytes?: number
    pathKind?: string
  } = {},
): void {
  recordTimelinePlaybackPerf({
    type: 'video-cache-background',
    assetId: videoId,
    cacheId,
    action,
    ...details,
  })
}

function startBackgroundCacheFromUrl(videoId: string, url: string): BackgroundCacheStatus {
  const cacheId = `${videoId}:bg-cache:${++backgroundCacheCounter}`
  const pathKind = classifyPlaybackMediaPath(url)
  const cacheKey = getVideoCacheKey(videoId, url)

  if (cachingInProgress.has(cacheKey) || cachingInProgress.has(videoId)) {
    recordBackgroundCacheEvent(videoId, cacheId, 'deduped', { reason: 'already-in-progress', pathKind })
    return 'already-in-progress'
  }

  if (getFromMemoryCache(videoId, url)) {
    recordBackgroundCacheEvent(videoId, cacheId, 'skipped', { reason: 'memory-cache-hit', pathKind })
    return 'skipped'
  }

  if (isTimelinePlaybackActive()) {
    recordBackgroundCacheEvent(videoId, cacheId, 'skipped', { reason: 'timeline-playback-active', pathKind })
    const cancel = schedulePlaybackAwareBackgroundWork(
      `video-cache-background:${cacheKey}`,
      'video-cache-background',
      () => {
        deferredBackgroundCacheCancels.delete(cacheKey)
        startBackgroundCacheFromUrl(videoId, url)
      },
    )
    deferredBackgroundCacheCancels.set(cacheKey, cancel)
    return 'skipped'
  }

  cachingInProgress.add(cacheKey)
  recordBackgroundCacheEvent(videoId, cacheId, 'started', { pathKind })
  backgroundCacheFromUrl(videoId, url, cacheId, pathKind).catch(err =>
    console.warn('[VideoCache] Background cache failed:', err),
  )
  return 'started'
}

async function backgroundCacheFromUrl(videoId: string, url: string, cacheId: string, pathKind: string): Promise<void> {
  const startedAt = performance.now()

  try {
    const fetchStart = performance.now()
    const response = await fetch(url)
    recordBackgroundCacheEvent(videoId, cacheId, 'stage', {
      stage: 'fetch',
      elapsedMs: performance.now() - fetchStart,
      status: response.status,
      pathKind,
    })
    if (!response.ok) {
      console.warn(
        `[VideoCache] Background cache skipped for ${videoId.slice(0, 8)}: HTTP ${response.status} ${safeUrlForLog(url)}`,
      )
      recordBackgroundCacheEvent(videoId, cacheId, 'skipped', {
        reason: 'http-status',
        status: response.status,
        elapsedMs: performance.now() - startedAt,
        pathKind,
      })
      return
    }
    const contentType = response.headers.get('content-type') || 'video/mp4'
    const blobStart = performance.now()
    const blob = await response.blob()
    recordBackgroundCacheEvent(videoId, cacheId, 'stage', {
      stage: 'blob-creation',
      elapsedMs: performance.now() - blobStart,
      bytes: blob.size,
      pathKind,
    })

    // Add to memory cache first (faster subsequent access)
    addToMemoryCache(videoId, blob, url)
    // Persist to IndexedDB
    const indexedDBStart = performance.now()
    await cacheVideo(videoId, blob, contentType, url)
    recordBackgroundCacheEvent(videoId, cacheId, 'stage', {
      stage: 'indexeddb-write',
      elapsedMs: performance.now() - indexedDBStart,
      bytes: blob.size,
      pathKind,
    })
    recordBackgroundCacheEvent(videoId, cacheId, 'completed', {
      elapsedMs: performance.now() - startedAt,
      bytes: blob.size,
      pathKind,
    })
  } catch (err) {
    console.warn(`[VideoCache] Background cache failed for ${videoId.slice(0, 8)}:`, err)
    recordBackgroundCacheEvent(videoId, cacheId, 'failed', {
      reason: err instanceof Error ? err.name || err.message : String(err),
      elapsedMs: performance.now() - startedAt,
      pathKind,
    })
  } finally {
    cachingInProgress.delete(getVideoCacheKey(videoId, url))
  }
}

// =============================================================================
// Blob URL Lifecycle Management
// =============================================================================

const activeBlobUrls = new Map<string, string>() // cache key -> blob URL

/**
 * Create a blob URL and track it for cleanup
 */
function createTrackedBlobUrl(videoId: string, blob: Blob, sourceUrl?: string): string {
  const cacheKey = getVideoCacheKey(videoId, sourceUrl)
  // Revoke old blob URL if exists
  const oldUrl = activeBlobUrls.get(cacheKey)
  if (oldUrl) {
    URL.revokeObjectURL(oldUrl)
  }

  const blobUrl = URL.createObjectURL(blob)
  activeBlobUrls.set(cacheKey, blobUrl)
  return blobUrl
}

/**
 * Revoke blob URL for a specific video. When sourceUrl is provided, revoke
 * only that exact media object so other proxy rungs can keep playing.
 */
export function revokeBlobUrl(videoId: string, sourceUrl?: string): void {
  if (sourceUrl) {
    const cacheKey = getVideoCacheKey(videoId, sourceUrl)
    const blobUrl = activeBlobUrls.get(cacheKey)
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl)
      activeBlobUrls.delete(cacheKey)
    }
    return
  }

  for (const [cacheKey, blobUrl] of [...activeBlobUrls.entries()]) {
    if (cacheKey === videoId || cacheKey.startsWith(`${videoId}::`)) {
      URL.revokeObjectURL(blobUrl)
      activeBlobUrls.delete(cacheKey)
    }
  }
}

export function revokeBlobUrlForSource(source: VideoCachePlaybackSource): void {
  revokeBlobUrl(source.videoId, source.url)
}

// =============================================================================
// IndexedDB Operations
// =============================================================================

/**
 * Initialize or get the IndexedDB database
 */
function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      console.error('[VideoCache] Failed to open database:', request.error)
      reject(request.error)
    }

    request.onsuccess = () => {
      resolve(request.result)
    }

    request.onupgradeneeded = event => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(METADATA_STORE_NAME)) {
        db.createObjectStore(METADATA_STORE_NAME, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(CACHE_KEY_STORE_NAME)) {
        const store = db.createObjectStore(CACHE_KEY_STORE_NAME, { keyPath: 'cacheKey' })
        store.createIndex(CACHE_KEY_VIDEO_ID_INDEX, 'id', { unique: false })
        store.createIndex(CACHE_KEY_STORAGE_IDENTITY_INDEX, 'storageIdentity', { unique: false })
      }
      if (!db.objectStoreNames.contains(CACHE_KEY_METADATA_STORE_NAME)) {
        const store = db.createObjectStore(CACHE_KEY_METADATA_STORE_NAME, { keyPath: 'cacheKey' })
        store.createIndex(CACHE_KEY_VIDEO_ID_INDEX, 'id', { unique: false })
        store.createIndex(CACHE_KEY_STORAGE_IDENTITY_INDEX, 'storageIdentity', { unique: false })
      }
    }
  })

  return dbPromise
}

function hasMetadataStore(db: IDBDatabase): boolean {
  return db.objectStoreNames.contains(METADATA_STORE_NAME)
}

function hasCacheKeyStores(db: IDBDatabase): boolean {
  return (
    db.objectStoreNames.contains(CACHE_KEY_STORE_NAME) && db.objectStoreNames.contains(CACHE_KEY_METADATA_STORE_NAME)
  )
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function getCachedVideoMetadataByCacheKey(
  db: IDBDatabase,
  cacheKey: string,
): Promise<CachedVideoMetadata | null> {
  const transaction = db.transaction(CACHE_KEY_METADATA_STORE_NAME, 'readonly')
  const store = transaction.objectStore(CACHE_KEY_METADATA_STORE_NAME)
  return requestToPromise<CachedVideoMetadata | undefined>(store.get(cacheKey)).then(result => result ?? null)
}

async function getCachedVideoMetadataCandidates(db: IDBDatabase, videoId: string): Promise<CachedVideoMetadata[]> {
  const transaction = db.transaction(CACHE_KEY_METADATA_STORE_NAME, 'readonly')
  const store = transaction.objectStore(CACHE_KEY_METADATA_STORE_NAME)
  const index = store.index(CACHE_KEY_VIDEO_ID_INDEX)
  return requestToPromise<CachedVideoMetadata[]>(index.getAll(videoId)).catch(() => [])
}

async function getCachedVideosByVideoId(db: IDBDatabase, videoId: string): Promise<CachedVideo[]> {
  const transaction = db.transaction(CACHE_KEY_STORE_NAME, 'readonly')
  const store = transaction.objectStore(CACHE_KEY_STORE_NAME)
  const index = store.index(CACHE_KEY_VIDEO_ID_INDEX)
  return requestToPromise<CachedVideo[]>(index.getAll(videoId)).catch(() => [])
}

async function getLegacyCachedVideoMetadata(db: IDBDatabase, id: string): Promise<CachedVideoMetadata | null> {
  if (!hasMetadataStore(db)) return null
  const transaction = db.transaction(METADATA_STORE_NAME, 'readonly')
  const store = transaction.objectStore(METADATA_STORE_NAME)
  return requestToPromise<CachedVideoMetadata | undefined>(store.get(id)).then(result => result ?? null)
}

async function getLegacyCachedVideo(db: IDBDatabase, id: string): Promise<CachedVideo | null> {
  const transaction = db.transaction(STORE_NAME, 'readonly')
  const store = transaction.objectStore(STORE_NAME)
  return requestToPromise<CachedVideo | undefined>(store.get(id)).then(result => result ?? null)
}

type MetadataLookup = {
  exact: CachedVideoMetadata | null
  stale: CachedVideoMetadata | null
}

async function lookupCachedVideoMetadata(videoId: string, sourceUrl: string): Promise<MetadataLookup> {
  try {
    const db = await getDB()
    let exact: CachedVideoMetadata | null = null
    let stale: CachedVideoMetadata | null = null

    if (hasCacheKeyStores(db)) {
      exact = await getCachedVideoMetadataByCacheKey(db, getVideoCacheKey(videoId, sourceUrl))
      if (!exact) {
        // Sort by `cachedAt` desc so the stale entry surfaced to diagnostics
        // reflects the most recently cached variant, matching the strategy in
        // `getCachedVideoMetadata` / `getAnyCachedVideo` further down the file.
        const candidates = await getCachedVideoMetadataCandidates(db, videoId)
        stale =
          candidates
            .sort((a, b) => b.cachedAt - a.cachedAt)
            .find(candidate => !isVideoCacheMetadataExactHit(candidate, videoId, sourceUrl)) ?? null
      }
    }

    const legacy = await getLegacyCachedVideoMetadata(db, videoId)
    if (legacy) {
      if (isVideoCacheMetadataExactHit(legacy, videoId, sourceUrl)) {
        exact ??= legacy
      } else {
        stale ??= legacy
      }
    }

    return { exact, stale }
  } catch {
    return { exact: null, stale: null }
  }
}

async function getCachedVideoMetadata(id: string, sourceUrl?: string): Promise<CachedVideoMetadata | null> {
  try {
    const db = await getDB()
    if (sourceUrl && hasCacheKeyStores(db)) {
      const exact = await getCachedVideoMetadataByCacheKey(db, getVideoCacheKey(id, sourceUrl))
      if (exact) return exact
      const candidates = await getCachedVideoMetadataCandidates(db, id)
      if (candidates.length > 0) return candidates.sort((a, b) => b.cachedAt - a.cachedAt)[0]
    }
    return getLegacyCachedVideoMetadata(db, id)
  } catch {
    return null
  }
}

async function putCachedVideoMetadata(metadata: CachedVideoMetadata): Promise<void> {
  try {
    const db = await getDB()
    if (!hasCacheKeyStores(db)) return
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(CACHE_KEY_METADATA_STORE_NAME, 'readwrite')
      const store = transaction.objectStore(CACHE_KEY_METADATA_STORE_NAME)
      const request = store.put({
        ...metadata,
        cacheKey: metadata.cacheKey ?? getVideoCacheKey(metadata.id, metadata.sourceUrl),
      })

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } catch (error) {
    console.error('[VideoCache] Failed to cache video metadata:', error)
  }
}

export async function hasExactCachedVideo(videoId: string, sourceUrl: string): Promise<boolean> {
  const lookup = await lookupCachedVideoMetadata(videoId, sourceUrl)
  if (lookup.exact && isVideoCacheMetadataExactHit(lookup.exact, videoId, sourceUrl)) return true
  if (lookup.stale) return false

  // DB v2 added metadata stores without backfilling older cached blobs. On a
  // metadata miss, fall back to the blob store once so legacy exact hits are
  // not re-downloaded, then repair the missing metadata opportunistically.
  const cached = await getCachedVideo(videoId, sourceUrl)
  if (!cached) return false
  void putCachedVideoMetadata(makeCacheMetadata(videoId, cached.blob, cached.contentType, cached.sourceUrl))
  return true
}

export function hasExactCachedVideoForSource(source: VideoCachePlaybackSource): Promise<boolean> {
  return hasExactCachedVideo(source.videoId, source.url)
}

export async function diagnoseExactVideoCache(
  videoId: string,
  sourceUrl: string,
  sourceKey?: string,
): Promise<ExactVideoCacheDiagnostic> {
  const requestedProxyResolution = getVideoCacheProxyResolution(sourceUrl)
  const cacheKey = getVideoCacheKey(videoId, sourceUrl)
  const storageIdentity = getVideoCacheStorageIdentity(sourceUrl)
  try {
    const lookup = await lookupCachedVideoMetadata(videoId, sourceUrl)
    const metadata = lookup.exact ?? lookup.stale
    const cachedProxyResolution = getVideoCacheProxyResolution(metadata?.storageIdentity ?? metadata?.sourceUrl)
    if (lookup.exact) {
      return {
        videoId,
        sourceKey,
        cacheKey,
        storageIdentity,
        status: 'exact',
        requestedProxyResolution,
        cachedProxyResolution,
      }
    }
    if (lookup.stale) {
      return {
        videoId,
        sourceKey,
        cacheKey,
        storageIdentity,
        status: 'stale',
        requestedProxyResolution,
        cachedProxyResolution,
        mismatchReason: getVideoCacheMetadataMismatchReason(lookup.stale, videoId, sourceUrl),
      }
    }
    return {
      videoId,
      sourceKey,
      cacheKey,
      storageIdentity,
      status: 'miss',
      requestedProxyResolution,
      cachedProxyResolution: null,
      mismatchReason: 'metadata-miss',
    }
  } catch {
    return {
      videoId,
      sourceKey,
      cacheKey,
      storageIdentity,
      status: 'error',
      requestedProxyResolution,
      cachedProxyResolution: null,
      mismatchReason: 'metadata-error',
    }
  }
}

export function diagnoseExactVideoCacheForSource(source: VideoCachePlaybackSource): Promise<ExactVideoCacheDiagnostic> {
  return diagnoseExactVideoCache(source.videoId, source.url, source.key)
}

export async function fillExactVideoCache(
  videoId: string,
  sourceUrl: string,
  options: { promoteToMemory?: boolean; signal?: AbortSignal; sourceKey?: string } = {},
): Promise<ExactVideoCacheFillResult> {
  const startedAt = performance.now()
  const requestedProxyResolution = getVideoCacheProxyResolution(sourceUrl)
  const pathKind = classifyPlaybackMediaPath(sourceUrl)
  const cacheKey = getVideoCacheKey(videoId, sourceUrl)

  if (await hasExactCachedVideo(videoId, sourceUrl)) {
    return {
      videoId,
      sourceKey: options.sourceKey,
      cacheKey,
      status: 'already-cached',
      elapsedMs: performance.now() - startedAt,
      requestedProxyResolution,
      pathKind,
    }
  }

  if (cachingInProgress.has(cacheKey) || cachingInProgress.has(videoId)) {
    return {
      videoId,
      sourceKey: options.sourceKey,
      cacheKey,
      status: 'deduped-in-progress',
      elapsedMs: performance.now() - startedAt,
      requestedProxyResolution,
      pathKind,
    }
  }

  cachingInProgress.add(cacheKey)
  try {
    const fetchStartedAt = performance.now()
    const response = await fetch(sourceUrl, { cache: 'no-store', signal: options.signal })
    const fetchMs = performance.now() - fetchStartedAt
    if (!response.ok) {
      return {
        videoId,
        sourceKey: options.sourceKey,
        cacheKey,
        status: 'failed',
        elapsedMs: performance.now() - startedAt,
        fetchMs,
        requestedProxyResolution,
        pathKind,
        errorName: `HTTP_${response.status}`,
      }
    }

    const contentType = response.headers.get('content-type') || 'video/mp4'
    const blobStartedAt = performance.now()
    const blob = await response.blob()
    const blobMs = performance.now() - blobStartedAt

    const writeStartedAt = performance.now()
    await cacheVideo(videoId, blob, contentType, sourceUrl)
    const writeMs = performance.now() - writeStartedAt
    if (options.promoteToMemory) {
      addToMemoryCache(videoId, blob, sourceUrl)
    }

    return {
      videoId,
      sourceKey: options.sourceKey,
      cacheKey,
      status: 'cached',
      elapsedMs: performance.now() - startedAt,
      fetchMs,
      blobMs,
      writeMs,
      bytes: blob.size,
      requestedProxyResolution,
      pathKind,
    }
  } catch (error) {
    return {
      videoId,
      sourceKey: options.sourceKey,
      cacheKey,
      status: error instanceof StorageQuotaError ? 'quota-exceeded' : 'failed',
      elapsedMs: performance.now() - startedAt,
      requestedProxyResolution,
      pathKind,
      errorName: error instanceof Error ? error.name : 'UnknownError',
    }
  } finally {
    cachingInProgress.delete(cacheKey)
  }
}

export function fillExactVideoCacheForSource(
  source: VideoCachePlaybackSource,
  options: { promoteToMemory?: boolean; signal?: AbortSignal } = {},
): Promise<ExactVideoCacheFillResult> {
  return fillExactVideoCache(source.videoId, source.url, { ...options, sourceKey: source.key })
}

/**
 * Get an exact cached video by resolved media identity.
 */
async function getCachedVideo(id: string, sourceUrl: string): Promise<CachedVideo | null> {
  try {
    const db = await getDB()
    if (hasCacheKeyStores(db)) {
      const transaction = db.transaction(CACHE_KEY_STORE_NAME, 'readonly')
      const store = transaction.objectStore(CACHE_KEY_STORE_NAME)
      const exact = await requestToPromise<CachedVideo | undefined>(store.get(getVideoCacheKey(id, sourceUrl))).then(
        result => result ?? null,
      )
      if (exact && isCachedVideoExactHit(exact, id, sourceUrl)) return exact
    }

    const legacy = await getLegacyCachedVideo(db, id)
    return legacy && isCachedVideoExactHit(legacy, id, sourceUrl) ? legacy : null
  } catch {
    return null
  }
}

async function getAnyCachedVideo(id: string): Promise<CachedVideo | null> {
  try {
    const db = await getDB()
    if (hasCacheKeyStores(db)) {
      const candidates = await getCachedVideosByVideoId(db, id)
      const newest = candidates.sort((a, b) => b.cachedAt - a.cachedAt)[0]
      if (newest) return newest
    }
    return getLegacyCachedVideo(db, id)
  } catch {
    return null
  }
}

/** Error thrown when IndexedDB storage quota is exceeded */
export class StorageQuotaError extends Error {
  constructor(videoId: string) {
    super(`Storage quota exceeded while caching ${videoId}`)
    this.name = 'StorageQuotaError'
  }
}

/**
 * Store a video in the cache.
 * Throws StorageQuotaError if the browser's IndexedDB quota is exceeded.
 */
export async function cacheVideo(id: string, blob: Blob, contentType: string, sourceUrl?: string): Promise<void> {
  try {
    const db = await getDB()
    return new Promise((resolve, reject) => {
      const useCacheKeyStores = hasCacheKeyStores(db)
      const storeNames = useCacheKeyStores
        ? [CACHE_KEY_STORE_NAME, CACHE_KEY_METADATA_STORE_NAME]
        : hasMetadataStore(db)
          ? [STORE_NAME, METADATA_STORE_NAME]
          : [STORE_NAME]
      const transaction = db.transaction(storeNames, 'readwrite')
      const metadata = makeCacheMetadata(id, blob, contentType, sourceUrl)
      const cachedVideo: CachedVideo = {
        cacheKey: metadata.cacheKey,
        id,
        blob,
        contentType,
        cachedAt: metadata.cachedAt,
        sourceUrl,
        storageIdentity: metadata.storageIdentity,
      }
      const store = transaction.objectStore(useCacheKeyStores ? CACHE_KEY_STORE_NAME : STORE_NAME)
      const request = store.put(
        useCacheKeyStores ? cachedVideo : { id, blob, contentType, cachedAt: metadata.cachedAt, sourceUrl },
      )
      if (useCacheKeyStores) {
        transaction.objectStore(CACHE_KEY_METADATA_STORE_NAME).put(metadata)
      } else if (hasMetadataStore(db)) {
        transaction.objectStore(METADATA_STORE_NAME).put(metadata)
      }

      transaction.oncomplete = () => {
        resolve()
      }

      transaction.onerror = () => {
        const error = transaction.error || request.error
        if (error?.name === 'QuotaExceededError') {
          reject(new StorageQuotaError(id))
        } else {
          console.error('[VideoCache] Failed to cache video:', error)
          reject(error)
        }
      }
    })
  } catch (err) {
    if (err instanceof StorageQuotaError) throw err
    console.error('[VideoCache] Failed to cache video:', err)
  }
}

/**
 * Get URL for a video with intelligent caching strategy.
 *
 * Strategy (checkIndexedDB=false, hot path for scrubbing):
 * 1. Check memory cache (instant) → return blob URL
 * 2. Return R2 URL directly + start background cache to IndexedDB
 *
 * Strategy (checkIndexedDB=true, cold path for prewarming):
 * 1. Check memory cache (instant) → return blob URL
 * 2. Check IndexedDB (100-300ms) → return blob URL + promote to memory
 * 3. Return R2 URL directly + start background cache to IndexedDB
 *
 * This avoids double downloads while prioritizing responsiveness on the hot path.
 *
 * @param videoId - Unique identifier for the video
 * @param r2Url - R2/CDN URL for the video
 * @param options - Configuration options
 * @param options.checkIndexedDB - Whether to check IndexedDB (slower but more thorough). Default: false for hot path performance.
 * @param options.allowIndexedDBExactHit - Allow guarded active-path metadata lookup and exact-hit Blob read without global offline mode.
 * @returns URL to use with mediabunny (either blob: URL or https: URL)
 */
export async function getSinkUrl(
  videoId: string,
  r2Url: string,
  options: {
    checkIndexedDB?: boolean
    pipelineId?: string
    allowIndexedDBExactHit?: boolean
    allowBackgroundCache?: boolean
    accessPattern?: 'playback' | 'prewarm' | 'scrub' | 'audio' | 'other'
    sourceKey?: string
  } = {},
): Promise<string> {
  const startedAt = performance.now()
  const {
    checkIndexedDB = getOfflineMode(),
    pipelineId,
    accessPattern = 'other',
    allowIndexedDBExactHit = accessPattern === 'playback' && isActiveIndexedDBExactHitEnabled(),
    allowBackgroundCache = true,
    sourceKey,
  } = options
  const offlineMode = getOfflineMode()
  const pathKind = classifyPlaybackMediaPath(r2Url)
  const cacheKey = getVideoCacheKey(videoId, r2Url)
  const storageIdentity = getVideoCacheStorageIdentity(r2Url)

  const recordResolution = (cacheSource: VideoCacheSource, details: CacheResolutionDiagnostics = {}) => {
    const requestedProxyResolution = details.requestedProxyResolution ?? getVideoCacheProxyResolution(r2Url)
    recordTimelinePlaybackPerf({
      type: 'sink-url-resolved',
      assetId: videoId,
      pipelineId,
      elapsedMs: performance.now() - startedAt,
      cacheSource,
      backgroundCacheStatus: details.backgroundCacheStatus,
      checkIndexedDB,
      offlineMode,
      blobUrlReplaced: details.blobUrlReplaced,
      cachePolicy: details.cachePolicy ?? 'default',
      sourceKey: details.sourceKey ?? sourceKey,
      cacheKey: details.cacheKey ?? cacheKey,
      storageIdentity: details.storageIdentity ?? storageIdentity,
      indexedDBMetadataStatus: details.indexedDBMetadataStatus,
      indexedDBMetadataMs: details.indexedDBMetadataMs,
      indexedDBBlobReadMs: details.indexedDBBlobReadMs,
      indexedDBSkippedReason: details.indexedDBSkippedReason,
      requestedProxyResolution,
      cachedProxyResolution: details.cachedProxyResolution,
      cacheIdentityMismatchReason: details.cacheIdentityMismatchReason,
      pathKind: cacheSource === 'direct-remote-url' ? pathKind : 'blob-url',
    })
  }

  // 1. Check memory cache (instant access, always check)
  const memoryBlob = getFromMemoryCache(videoId, r2Url)
  if (memoryBlob) {
    const blobUrlReplaced = activeBlobUrls.has(cacheKey)
    const blobUrl = createTrackedBlobUrl(videoId, memoryBlob, r2Url)
    recordResolution('memory-blob', {
      blobUrlReplaced,
      cachedProxyResolution: getVideoCacheProxyResolution(r2Url),
    })
    return blobUrl
  }

  // 2a. Guarded active-path exact-hit policy: check lightweight metadata first,
  // then read the Blob only if metadata predicts an exact local candidate.
  if (!checkIndexedDB && allowIndexedDBExactHit) {
    const metadataStart = performance.now()
    let metadataStatus: CacheMetadataStatus = 'unknown'
    let metadataMs = 0
    let metadata: CachedVideoMetadata | null = null
    try {
      const lookup = await lookupCachedVideoMetadata(videoId, r2Url)
      metadataMs = performance.now() - metadataStart
      metadata = lookup.exact ?? lookup.stale
      if (lookup.exact) {
        metadataStatus = 'hit'
      } else if (lookup.stale) {
        metadataStatus = 'stale'
      } else {
        metadataStatus = 'miss'
      }
    } catch {
      metadataMs = performance.now() - metadataStart
      metadataStatus = 'error'
    }

    if (metadataStatus === 'hit') {
      const blobReadStart = performance.now()
      const cached = await getCachedVideo(videoId, r2Url)
      const blobReadMs = performance.now() - blobReadStart
      if (cached && isCachedVideoExactHit(cached, videoId, r2Url)) {
        addToMemoryCache(videoId, cached.blob, cached.sourceUrl)
        const blobUrlReplaced = activeBlobUrls.has(cacheKey)
        const blobUrl = createTrackedBlobUrl(videoId, cached.blob, r2Url)
        recordResolution('indexeddb-exact-hit', {
          blobUrlReplaced,
          cachePolicy: 'active-exact-hit',
          indexedDBMetadataStatus: metadataStatus,
          indexedDBMetadataMs: metadataMs,
          indexedDBBlobReadMs: blobReadMs,
          cachedProxyResolution: getVideoCacheProxyResolution(metadata?.storageIdentity ?? metadata?.sourceUrl),
        })
        return blobUrl
      }

      await evictFromCache(videoId, r2Url).catch(() => {})
      metadataStatus = 'stale'
      recordResolution('direct-remote-url', {
        backgroundCacheStatus: allowBackgroundCache ? startBackgroundCacheFromUrl(videoId, r2Url) : 'skipped',
        cachePolicy: 'active-exact-hit',
        indexedDBMetadataStatus: metadataStatus,
        indexedDBMetadataMs: metadataMs,
        indexedDBBlobReadMs: blobReadMs,
        indexedDBSkippedReason: cached ? 'blob-metadata-mismatch' : 'metadata-without-blob',
        cachedProxyResolution: getVideoCacheProxyResolution(metadata?.storageIdentity ?? metadata?.sourceUrl),
        cacheIdentityMismatchReason: getVideoCacheMetadataMismatchReason(metadata, videoId, r2Url),
      })
      return r2Url
    }

    const backgroundCacheStatus = allowBackgroundCache ? startBackgroundCacheFromUrl(videoId, r2Url) : 'skipped'
    const mismatchReason =
      metadataStatus === 'stale' ? getVideoCacheMetadataMismatchReason(metadata, videoId, r2Url) : undefined
    recordResolution('direct-remote-url', {
      backgroundCacheStatus,
      cachePolicy: 'active-exact-hit',
      indexedDBMetadataStatus: metadataStatus,
      indexedDBMetadataMs: metadataMs,
      indexedDBSkippedReason:
        metadataStatus === 'miss'
          ? 'metadata-miss'
          : metadataStatus === 'stale'
            ? getVideoCacheMetadataMismatchReason(metadata, videoId, r2Url)
            : 'metadata-unavailable',
      cachedProxyResolution: getVideoCacheProxyResolution(metadata?.storageIdentity ?? metadata?.sourceUrl),
      cacheIdentityMismatchReason: mismatchReason,
    })
    return r2Url
  }

  // 2b. Check IndexedDB only if requested (adds 100-300ms latency)
  if (checkIndexedDB) {
    const blobReadStart = performance.now()
    const cached = await getCachedVideo(videoId, r2Url)
    const blobReadMs = performance.now() - blobReadStart
    if (cached) {
      if (isCachedVideoExactHit(cached, videoId, r2Url)) {
        void putCachedVideoMetadata(makeCacheMetadata(videoId, cached.blob, cached.contentType, cached.sourceUrl))
        // Exact match — promote to memory cache for faster subsequent access
        addToMemoryCache(videoId, cached.blob, cached.sourceUrl)
        const blobUrlReplaced = activeBlobUrls.has(cacheKey)
        const blobUrl = createTrackedBlobUrl(videoId, cached.blob, r2Url)
        recordResolution('indexeddb-exact-hit', {
          blobUrlReplaced,
          indexedDBBlobReadMs: blobReadMs,
          cachedProxyResolution: getVideoCacheProxyResolution(cached.storageIdentity ?? cached.sourceUrl),
        })
        return blobUrl
      }
    }

    const staleCached = offlineMode ? await getAnyCachedVideo(videoId) : null
    if (staleCached && !isCachedVideoExactHit(staleCached, videoId, r2Url)) {
      // Offline mode: serve the cached version even if URL doesn't match
      // (e.g., cached at 720p but now requesting 480p). A different resolution
      // is better than a network error when offline.
      addToMemoryCache(videoId, staleCached.blob, staleCached.sourceUrl)
      const staleCacheKey = getVideoCacheKey(videoId, staleCached.sourceUrl)
      const blobUrlReplaced = activeBlobUrls.has(staleCacheKey)
      const blobUrl = createTrackedBlobUrl(videoId, staleCached.blob, staleCached.sourceUrl)
      recordResolution('indexeddb-stale-offline-hit', {
        blobUrlReplaced,
        indexedDBBlobReadMs: blobReadMs,
        cachedProxyResolution: getVideoCacheProxyResolution(staleCached.storageIdentity ?? staleCached.sourceUrl),
        indexedDBSkippedReason: getVideoCacheMetadataMismatchReason(
          {
            id: staleCached.id,
            sourceUrl: staleCached.sourceUrl,
            storageIdentity:
              staleCached.storageIdentity ?? getVideoCacheStorageIdentity(staleCached.sourceUrl) ?? undefined,
          },
          videoId,
          r2Url,
        ),
        cacheIdentityMismatchReason: getVideoCacheMetadataMismatchReason(
          {
            id: staleCached.id,
            sourceUrl: staleCached.sourceUrl,
            storageIdentity:
              staleCached.storageIdentity ?? getVideoCacheStorageIdentity(staleCached.sourceUrl) ?? undefined,
          },
          videoId,
          r2Url,
        ),
      })
      return blobUrl
    }
  }

  // 3. Cache miss - use R2 directly and cache in background
  const backgroundCacheStatus = allowBackgroundCache ? startBackgroundCacheFromUrl(videoId, r2Url) : 'skipped'
  recordResolution('direct-remote-url', { backgroundCacheStatus })
  return r2Url
}

export function getSinkUrlForSource(
  source: VideoCachePlaybackSource,
  options: Omit<Parameters<typeof getSinkUrl>[2], 'sourceKey'> = {},
): Promise<string> {
  return getSinkUrl(source.videoId, source.url, { ...options, sourceKey: source.key })
}

/**
 * Download and cache a video, returning a blob URL
 * If already cached, returns the cached blob URL
 *
 * @deprecated Use getSinkUrl instead for better performance
 */
export async function getVideoUrl(filename: string): Promise<string> {
  return getSinkUrl(filename, getStorageUrl(filename))
}

/**
 * Preload a video into cache without returning a URL
 * Uses background caching to avoid double downloads
 */
export async function preloadVideo(videoId: string, url?: string): Promise<void> {
  const r2Url = url || getStorageUrl(videoId)

  // Check if already cached in memory or IndexedDB
  if (getFromMemoryCache(videoId, r2Url)) {
    return
  }

  const metadata = await getCachedVideoMetadata(videoId, r2Url)
  if (metadata && isVideoCacheMetadataExactHit(metadata, videoId, r2Url)) {
    const cached = await getCachedVideo(videoId, r2Url)
    if (!cached) {
      await evictFromCache(videoId, r2Url).catch(() => {})
    } else {
      // Promote to memory cache for faster access
      addToMemoryCache(videoId, cached.blob, cached.sourceUrl)
      return
    }
  }

  const cached = await getCachedVideo(videoId, r2Url)
  if (cached) {
    void putCachedVideoMetadata(makeCacheMetadata(videoId, cached.blob, cached.contentType, cached.sourceUrl))
    // Promote to memory cache for faster access
    addToMemoryCache(videoId, cached.blob, cached.sourceUrl)
    return
  }

  // Start background caching (async, non-blocking)
  startBackgroundCacheFromUrl(videoId, r2Url)
}

/**
 * Clear all cached videos
 */
export async function clearVideoCache(): Promise<void> {
  cancelAllDeferredBackgroundCaches()
  try {
    const db = await getDB()
    return new Promise((resolve, reject) => {
      const storeNames = [
        STORE_NAME,
        ...(hasMetadataStore(db) ? [METADATA_STORE_NAME] : []),
        ...(hasCacheKeyStores(db) ? [CACHE_KEY_STORE_NAME, CACHE_KEY_METADATA_STORE_NAME] : []),
      ]
      const transaction = db.transaction(storeNames, 'readwrite')
      const request = transaction.objectStore(STORE_NAME).clear()
      if (hasMetadataStore(db)) transaction.objectStore(METADATA_STORE_NAME).clear()
      if (hasCacheKeyStores(db)) {
        transaction.objectStore(CACHE_KEY_STORE_NAME).clear()
        transaction.objectStore(CACHE_KEY_METADATA_STORE_NAME).clear()
      }

      transaction.oncomplete = () => {
        resolve()
      }

      transaction.onerror = () => {
        console.error('[VideoCache] Failed to clear cache:', transaction.error || request.error)
        reject(transaction.error || request.error)
      }
    })
  } catch (err) {
    console.error('[VideoCache] Failed to clear cache:', err)
  }
}

/**
 * Get all cached video IDs
 */
export async function getAllCachedVideoIds(): Promise<string[]> {
  try {
    const db = await getDB()
    if (hasCacheKeyStores(db)) {
      const transaction = db.transaction(CACHE_KEY_METADATA_STORE_NAME, 'readonly')
      const store = transaction.objectStore(CACHE_KEY_METADATA_STORE_NAME)
      const metadata = await requestToPromise<CachedVideoMetadata[]>(store.getAll()).catch(() => [])
      if (metadata.length > 0) return [...new Set(metadata.map(item => item.id))]
    }
    const transaction = db.transaction(STORE_NAME, 'readonly')
    const store = transaction.objectStore(STORE_NAME)
    return requestToPromise<IDBValidKey[]>(store.getAllKeys())
      .then(keys => keys.map(String))
      .catch(() => [])
  } catch {
    return []
  }
}

/**
 * Get cache statistics including memory and IndexedDB
 */
export async function getCacheStats(): Promise<{
  count: number
  totalSize: number
  memoryCount: number
  memorySize: number
  indexedDBCount: number
  indexedDBSize: number
}> {
  try {
    const db = await getDB()
    const readStats = async (storeName: string) => {
      const transaction = db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const items = await requestToPromise<CachedVideo[]>(store.getAll()).catch(() => [])
      const totalSize = items.reduce((sum, item) => sum + item.blob.size, 0)
      return { count: items.length, totalSize }
    }
    const cacheKeyStats = hasCacheKeyStores(db) ? await readStats(CACHE_KEY_STORE_NAME) : { count: 0, totalSize: 0 }
    const indexedDBStats = cacheKeyStats.count > 0 ? cacheKeyStats : await readStats(STORE_NAME)

    return {
      count: indexedDBStats.count + memoryCache.size,
      totalSize: indexedDBStats.totalSize + memoryCacheSize,
      memoryCount: memoryCache.size,
      memorySize: memoryCacheSize,
      indexedDBCount: indexedDBStats.count,
      indexedDBSize: indexedDBStats.totalSize,
    }
  } catch {
    return {
      count: memoryCache.size,
      totalSize: memoryCacheSize,
      memoryCount: memoryCache.size,
      memorySize: memoryCacheSize,
      indexedDBCount: 0,
      indexedDBSize: 0,
    }
  }
}

/**
 * Evict a specific video from all caches (memory and IndexedDB).
 * Used when a video is detected as corrupted (e.g., sink creation timeout).
 */
export async function evictFromCache(videoId: string, sourceUrl?: string): Promise<void> {
  cancelDeferredBackgroundCachesForVideo(videoId, sourceUrl)

  // Remove from memory cache
  for (const [cacheKey, memoryEntry] of [...memoryCache.entries()]) {
    const shouldDelete = sourceUrl
      ? cacheKey === getVideoCacheKey(videoId, sourceUrl)
      : cacheKey === videoId || cacheKey.startsWith(`${videoId}::`)
    if (shouldDelete) {
      memoryCacheSize -= memoryEntry.blob.size
      memoryCache.delete(cacheKey)
    }
  }

  // Remove from IndexedDB
  try {
    const db = await getDB()
    let cacheKeysToDelete: IDBValidKey[] = sourceUrl ? [getVideoCacheKey(videoId, sourceUrl)] : []
    if (!sourceUrl && hasCacheKeyStores(db)) {
      const metadataTransaction = db.transaction(CACHE_KEY_METADATA_STORE_NAME, 'readonly')
      const metadataIndex = metadataTransaction
        .objectStore(CACHE_KEY_METADATA_STORE_NAME)
        .index(CACHE_KEY_VIDEO_ID_INDEX)
      cacheKeysToDelete = await requestToPromise<IDBValidKey[]>(metadataIndex.getAllKeys(videoId)).catch(() => [])
    }

    return new Promise<void>((resolve, reject) => {
      const storeNames = [
        STORE_NAME,
        ...(hasMetadataStore(db) ? [METADATA_STORE_NAME] : []),
        ...(hasCacheKeyStores(db) ? [CACHE_KEY_STORE_NAME, CACHE_KEY_METADATA_STORE_NAME] : []),
      ]
      const transaction = db.transaction(storeNames, 'readwrite')
      const legacyRequest = sourceUrl ? undefined : transaction.objectStore(STORE_NAME).delete(videoId)
      if (!sourceUrl && hasMetadataStore(db)) transaction.objectStore(METADATA_STORE_NAME).delete(videoId)
      if (hasCacheKeyStores(db)) {
        const videoStore = transaction.objectStore(CACHE_KEY_STORE_NAME)
        const metadataStore = transaction.objectStore(CACHE_KEY_METADATA_STORE_NAME)
        for (const cacheKey of cacheKeysToDelete) {
          videoStore.delete(cacheKey)
          metadataStore.delete(cacheKey)
        }
      }

      transaction.oncomplete = () => {
        resolve()
      }

      transaction.onerror = () => {
        console.error(`[VideoCache] Failed to evict from IndexedDB: ${videoId.slice(0, 8)}...`)
        reject(transaction.error || legacyRequest?.error)
      }
    })
  } catch (error) {
    console.error('[VideoCache] Failed to evict from IndexedDB:', error)
  }
}

export function evictFromCacheForSource(source: VideoCachePlaybackSource): Promise<void> {
  return evictFromCache(source.videoId, source.url)
}
